import { BehaviorSubject } from 'rxjs';

/**
 * Catches the browser's install offer before Angular is running.
 *
 * <p>Chrome fires `beforeinstallprompt` once, as soon as it decides the app is
 * installable — which can be before the first component exists. There is no way to ask
 * for it again afterwards, so if nothing is listening at that moment the offer is gone
 * for that page load and no "Add to home screen" prompt can ever be shown. Hence a
 * plain module imported for its side effect from `main.ts`, ahead of bootstrap, rather
 * than a listener inside a service.</p>
 *
 * <p>`preventDefault()` suppresses Chrome's own mini-infobar so ours is the only prompt
 * the user sees. Keeping the event is what lets us call `prompt()` later — the browser
 * only accepts that call in response to a real user gesture, which is why the event is
 * stored rather than used immediately.</p>
 */

/** The stored event, or null when the browser has not offered (or we already used it). */
export const installPrompt$ = new BehaviorSubject<any | null>(null);

/** Set once the app is running as an installed app, so nothing offers to install it again. */
export const appInstalled$ = new BehaviorSubject<boolean>(false);

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault();
    installPrompt$.next(event);
  });

  window.addEventListener('appinstalled', () => {
    installPrompt$.next(null);
    appInstalled$.next(true);
  });
}
